import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import Layout, { Container } from "../components/layout";
import TwoFactorToggle from "../components/manage2FA";

const allPermissionsList = [
  "employee_bankdetails",
  "employee_details",
  "employee_password",
  "employee_offerdetails",
  "employee_managerdetails",
  "download_applications",
  "employee_exitprocess",
  "download_employees",
  "candidtae",
  "dashboard",
  "payroll",
  "joinee",
  "employee",
  "trainee",
  "joinee",
  "application",
  "employee_exitprocess",
  "fms",
  "sales",
  "batch",
  "analytics",
  "online_application",
  "team",
  "candidate",
  "joinee",
  "trainee",
  "interview",
  "survey",
  "referral",
  "team",
  "resume",
  "grievance",
  "ijp_request",
  "recruiter",
  "payment",
  "interview",
  "analytic_recruitment",
  "cst",
  "csp"
];

const ManageTeam = () => {
  const { userObjId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [updatingRole, setUpdatingRole] = useState(false);
  const [roleUpdateMessage, setRoleUpdateMessage] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [dirtyPermissions, setDirtyPermissions] = useState({});


  // local edits before save
  const [draftPermissions, setDraftPermissions] = useState({});

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.post(`${apiurl}/api/user/get/${userObjId}`);
        if (res.data.success) {
          setUser(res.data.data);
          setSelectedRole(res.data.data.role || "");
          setPermissions(res.data.data.permissions || []);
          setTwoFactorEnabled(res.data.data.twoFactorAuth.enabled)
          // build draft from backend
          const draft = {};
          res.data.data.permissions.forEach((p) => {
            draft[p.resource] = {
              read: p.read,
              write: p.write,
              update: p.update,
              delete: p.delete,
            };
          });
          setDraftPermissions(draft);
        } else {
          setError("User not found.");
        }
      } catch (err) {
        setError("Something went wrong.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userObjId]);

  // update draft checkboxes
  // const togglePermission = (resource, perm) => {
  //   setDraftPermissions((prev) => {
  //     const current = prev[resource] || {
  //       read: false,
  //       write: false,
  //       update: false,
  //       delete: false,
  //     };
  //     let updated = { ...current, [perm]: !current[perm] };

  //     // ✅ auto enforce read if anything else checked
  //     if (perm !== "read" && updated[perm]) {
  //       updated.read = true;
  //     }
  //     // ✅ auto uncheck others if read is unchecked
  //     if (perm === "read" && !updated.read) {
  //       updated = { read: false, write: false, update: false, delete: false };
  //     }
  //     return { ...prev, [resource]: updated };
  //   });
  // };
  const togglePermission = (resource, perm) => {
  setDraftPermissions((prev) => {
    const current = prev[resource] || {
      read: false,
      write: false,
      update: false,
      delete: false,
    };
    let updated = { ...current, [perm]: !current[perm] };

    if (perm !== "read" && updated[perm]) {
      updated.read = true;
    }
    if (perm === "read" && !updated.read) {
      updated = { read: false, write: false, update: false, delete: false };
    }

    // mark resource dirty
    setDirtyPermissions((dirtyPrev) => ({
      ...dirtyPrev,
      [resource]: true,
    }));

    return { ...prev, [resource]: updated };
  });
};


  // save selected draft to backend
  // const handlePermissionSave = async () => {
  //   setLoadingPermissions(true);
  //   try {
  //     const payload = {
  //       userObjId,
  //       permissions: Object.entries(draftPermissions).map(
  //         ([resource, crud]) => ({
  //           resource,
  //           ...crud,
  //         })
  //       ),
  //     };
  //     const res = await axios.post(
  //       `${apiurl}/api/user/permission/add`,
  //       payload
  //     );
  //     if (res.data.success) {
  //       // refresh
  //       const userRes = await axios.post(`${apiurl}/api/user/get/${userObjId}`);
  //       setPermissions(userRes.data.data.permissions || []);
  //       setShowPermissionModal(false);
  //     }
  //   } catch (err) {
  //     console.error("Permission save failed", err);
  //   } finally {
  //     setLoadingPermissions(false);
  //   }
  // };

  const handlePermissionSave = async () => {
  setLoadingPermissions(true);
  try {
    const payload = {
      userObjId,
      permissions: Object.entries(draftPermissions)
        .filter(([resource]) => dirtyPermissions[resource]) // only changed
        .map(([resource, crud]) => ({
          resource,
          ...crud,
        })),
    };

    if (payload.permissions.length === 0) {
      setShowPermissionModal(false);
      return;
    }

    const res = await axios.post(`${apiurl}/api/user/permission/add`, payload);
    if (res.data.success === true) {
      const userRes = await axios.post(`${apiurl}/api/user/get/${userObjId}`);
      setPermissions(userRes.data.data.permissions || []);
      setShowPermissionModal(false);
      setDirtyPermissions({}); // reset dirty state
    }
  } catch (err) {
    console.error("Permission save failed", err);
  } finally {
    setLoadingPermissions(false);
  }
};


  const handlePermissionRemove = async (resource) => {
    if (!window.confirm(`Remove all permissions for ${resource}?`)) return;
    setLoadingPermissions(true);
    try {
      const res = await axios.post(`${apiurl}/api/user/permission/remove`, {
        userObjId,
        resource,
      });
      if (res.data.success === true) {
        // refresh
        const userRes = await axios.post(`${apiurl}/api/user/get/${userObjId}`);
        setPermissions(userRes.data.data.permissions || []);
        setDraftPermissions((prev) => {
          const copy = { ...prev };
          delete copy[resource];
          return copy;
        });
      }
    } catch (err) {
      console.error("Permission remove failed", err);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleRoleUpdate = async () => {
    setUpdatingRole(true);
    setRoleUpdateMessage("");
    try {
      const res = await axios.post(`${apiurl}/api/user/update-role`, {
        userObjId,
        role: selectedRole,
      });

      if (res.data.success) {
        setRoleUpdateMessage("Role updated successfully.");
        setUser((prev) => ({ ...prev, role: selectedRole }));
      } else {
        setRoleUpdateMessage("Failed to update role.");
      }
    } catch (err) {
      console.error(err);
      setRoleUpdateMessage("An error occurred while updating role.");
    } finally {
      setUpdatingRole(false);
    }
  };

// State for Two-Factor
const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
const [loading2FA, setLoading2FA] = useState(false);
const [twoFAMessage, setTwoFAMessage] = useState("");

// Update twoFactorEnabled when user data is fetched
useEffect(() => {
  if (user && user.hasOwnProperty("twoFactorEnabled")) {
    setTwoFactorEnabled(user.twoFactorEnabled);
  }
}, [user]);

const handleTwoFactorToggle = async () => {
  if (!user) return;
  setLoading2FA(true);
  setTwoFAMessage("");

  try {
    const payload = {
      enable: !twoFactorEnabled,
      email: user.email,
      userObjId: user._id,
    };

    const res = await axios.post(`${apiurl}/api/user/twofactor`, payload);

    if (res.data.success) {
      setTwoFactorEnabled(!twoFactorEnabled);
      setTwoFAMessage(
        !twoFactorEnabled
          ? "Two-Factor Authentication enabled successfully."
          : "Two-Factor Authentication disabled successfully."
      );
    } else {
      setTwoFAMessage("Failed to update Two-Factor Authentication.");
    }
  } catch (err) {
    console.error(err);
    setTwoFAMessage("Error while updating Two-Factor Authentication.");
  } finally {
    setLoading2FA(false);
  }
};


  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        {/* Account Status */}
        <div className="bg-white p-6 rounded-xl shadow-md my-3 border">
          <h3 className="text-xl font-medium mb-4">Account Status</h3>
          <div className="flex items-center gap-6">
            <span
              className={`font-semibold ${
                user.status === "active"
                  ? "text-green-600"
                  : user.status === "inactive"
                  ? "text-yellow-600"
                  : "text-red-600"
              }`}
            >
              {user.status}
            </span>
            <button
              onClick={async () => {
                try {
                  const newStatus =
                    user.status === "active" ? "inactive" : "active";
                  const res = await axios.post(
                    `${apiurl}/api/user/update-status`,
                    { userObjId: user._id, newStatus }
                  );
                  if (res.data.success) {
                    setUser((prev) => ({ ...prev, status: newStatus }));
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              className={`px-4 py-2 rounded-md text-white ${
                user.status === "active"
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-green-500 hover:bg-green-600"
              }`}
            >
              {user.status === "active" ? "Deactivate" : "Reactivate"}
            </button>
          </div>
        </div>

{/* Two-Factor Authentication */}
<div className="bg-white p-6 rounded-xl shadow-md my-3 border">
  <h3 className="text-xl font-medium mb-4">Two-Factor Authentication (2FA)</h3>
  <div className="flex items-center gap-6">
    <span
      className={`font-semibold ${
        twoFactorEnabled ? "text-green-600" : "text-red-600"
      }`}
    >
      {twoFactorEnabled ? "Enabled" : "Disabled"}
    </span>
    <button
      onClick={handleTwoFactorToggle}
      disabled={loading2FA}
      className={`px-4 py-2 rounded-md text-white ${
        twoFactorEnabled
          ? "bg-red-500 hover:bg-red-600"
          : "bg-green-500 hover:bg-green-600"
      }`}
    >
      {loading2FA
        ? "Processing..."
        : twoFactorEnabled
        ? "Disable 2FA"
        : "Enable 2FA"}
    </button>
  </div>
  {twoFAMessage && (
    <p className="text-sm text-gray-600 mt-2">{twoFAMessage}</p>
  )}
</div>

<TwoFactorToggle user={user} />



        {/* User Info */}
        <div className="bg-white p-6 my-2 border rounded-xl shadow-md grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium">Name</h3>
            <p className="text-gray-700">
              {user.firstName} {user.lastName}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium">Email</h3>
            <p className="text-gray-700">{user.email}</p>
          </div>
          <div>
            <h3 className="text-lg font-medium">Phone</h3>
            <p className="text-gray-700">{user.phone}</p>
          </div>
          <div>
            <h3 className="text-lg font-medium">Role</h3>
             <p className="text-gray-700">{user.role}</p>
            <div className="flex gap-3 items-center">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Role</option>
                <option value="admin">Admin</option>
                <option value="hr">HR</option>
                <option value="interviewer">Interviewer</option>
                <option value="trainer">Trainer</option>
                <option value="screener">Screener</option>
                <option value="operations">Operations</option>
                <option value="recruiter">Recruiter</option>
              </select>
              <button
                onClick={handleRoleUpdate}
                disabled={updatingRole}
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
              >
                {updatingRole ? "Updating..." : "Update"}
              </button>
            </div>
            {roleUpdateMessage && (
              <p className="text-sm text-gray-600 mt-2">{roleUpdateMessage}</p>
            )}
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white p-6 rounded-xl shadow-md my-2 border">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-medium">Permissions</h3>
            <button
              onClick={() => setShowPermissionModal(true)}
              className="bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600"
            >
              Manage Permissions
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {permissions.length > 0 ? (
              permissions.map((p) => (
                <div
                  key={p._id || p.resource}
                  className="bg-gray-100 p-3 rounded-lg shadow-sm w-full"
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold">{p.resource}</h4>
                    <button
                      onClick={() => handlePermissionRemove(p.resource)}
                      className="text-red-500 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {["read", "write", "update", "delete"].map((perm) => (
                      <span
                        key={perm}
                        className={`px-2 py-1 rounded text-xs text-center ${
                          p[perm]
                            ? "bg-green-200 text-green-700"
                            : "bg-red-200 text-red-700"
                        }`}
                      >
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No permissions assigned.</p>
            )}
          </div>
        </div>

        {/* Permissions Modal */}
        {showPermissionModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-xl p-6 shadow-lg w-full max-w-3xl">
              <h2 className="text-xl font-semibold mb-4">Manage Permissions</h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {allPermissionsList.map((resource) => {
                  const crud = draftPermissions[resource] || {
                    read: false,
                    write: false,
                    update: false,
                    delete: false,
                  };
                  return (
                    <div
                      key={resource}
                      className="border p-3 rounded-lg space-y-2"
                    >
                      <span className="capitalize font-medium">{resource}</span>
                      <div className="grid grid-cols-4 gap-3">
                        {["read", "write", "update", "delete"].map((perm) => (
                          <label
                            key={perm}
                            className="flex items-center gap-1 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={crud[perm]}
                              onChange={() => togglePermission(resource, perm)}
                            />
                            {perm}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePermissionSave}
                  disabled={loadingPermissions}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
                >
                  {loadingPermissions ? "Saving..." : "Update Permissions"}
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default ManageTeam;